import React, { useEffect, useMemo, useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { Sidebar } from "primereact/sidebar";
import { TbSettings } from "react-icons/tb";
import { toast } from "react-toastify";
import api from "../api/axios";
import { FiEdit2, FiTrash2 } from "react-icons/fi";

const PAGE_NAME = "HSN & GST";
const emptyForm = {
  name: "",
  classification_type: "goods",
  hsn_sac_code: "",
  gst_percentage: "",
  description: "",
  status: "active",
};
const DEFAULT_TAX_SETTINGS = {
  business_name: "Graphe Weddings",
  origin_state: "West Bengal",
  origin_country: "India",
  cgst_percentage: "9.00",
  sgst_percentage: "9.00",
  igst_percentage: "18.00",
  lut_number: "",
  international_tax_note:
    "Customs duties, import taxes and other taxes applicable in the customer's country, if any, must be paid by the customer.",
};

const headers = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const TaxClasses = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taxSettingsOpen, setTaxSettingsOpen] = useState(false);
  const [taxSettingsLoading, setTaxSettingsLoading] = useState(false);
  const [taxSettingsSaving, setTaxSettingsSaving] = useState(false);
  const [taxSettingsForm, setTaxSettingsForm] = useState(DEFAULT_TAX_SETTINGS);

  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) =>
      `${item.name} ${item.hsn_sac_code}`.toLowerCase().includes(term)
    );
  }, [items, search]);

  const showError = (error, fallback) => {
    if (error.response?.status !== 403) {
      toast.error(error.response?.data?.message || fallback);
    }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/products/tax-classes", {
        headers: headers("view"),
      });
      setItems(response.data?.data || []);
    } catch (error) {
      showError(error, "Failed to load HSN/SAC tax classes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // This initial load intentionally runs only when the page mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const rate = Number(form.gst_percentage);
    if (!form.name.trim() || !form.hsn_sac_code.trim()) {
      toast.error("Name and HSN/SAC code are required.");
      return;
    }
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      toast.error("GST percentage must be between 0 and 100.");
      return;
    }

    try {
      setSaving(true);
      const payload = { ...form, gst_percentage: rate };
      if (editingId) {
        await api.put(`/admin/products/tax-classes/${editingId}`, payload, {
          headers: headers("edit"),
        });
        toast.success("Tax class updated.");
      } else {
        await api.post("/admin/products/tax-classes", payload, {
          headers: headers("add"),
        });
        toast.success("Tax class created.");
      }
      resetForm();
      await fetchItems();
    } catch (error) {
      showError(error, "Failed to save tax class.");
    } finally {
      setSaving(false);
    }
  };

  const editItem = (item) => {
    setEditingId(item.tax_class_id);
    setForm({
      name: item.name || "",
      classification_type: item.classification_type || "goods",
      hsn_sac_code: item.hsn_sac_code || "",
      gst_percentage: item.gst_percentage ?? "",
      description: item.description || "",
      status: item.status || "active",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete ${item.hsn_sac_code} - ${item.name}?`)) return;
    try {
      await api.delete(`/admin/products/tax-classes/${item.tax_class_id}`, {
        headers: headers("delete"),
      });
      toast.success("Tax class deleted.");
      await fetchItems();
    } catch (error) {
      showError(error, "Failed to delete tax class.");
    }
  };

  const openTaxSettings = async () => {
    setTaxSettingsOpen(true);
    setTaxSettingsLoading(true);

    try {
      const response = await api.get("/admin/invoice/tax-settings", {
        headers: headers("view"),
      });
      const settings = response.data?.data || DEFAULT_TAX_SETTINGS;

      setTaxSettingsForm({
        business_name:
          settings.business_name || DEFAULT_TAX_SETTINGS.business_name,
        origin_state:
          settings.origin_state || DEFAULT_TAX_SETTINGS.origin_state,
        origin_country:
          settings.origin_country || DEFAULT_TAX_SETTINGS.origin_country,
        cgst_percentage: String(settings.cgst_percentage ?? 9),
        sgst_percentage: String(settings.sgst_percentage ?? 9),
        igst_percentage: String(settings.igst_percentage ?? 18),
        lut_number: settings.lut_number || "",
        international_tax_note:
          settings.international_tax_note ||
          DEFAULT_TAX_SETTINGS.international_tax_note,
      });
    } catch (error) {
      showError(error, "Failed to load invoice tax settings.");
      setTaxSettingsOpen(false);
    } finally {
      setTaxSettingsLoading(false);
    }
  };

  const updateTaxSettingsField = (field, value) => {
    setTaxSettingsForm((current) => ({ ...current, [field]: value }));
  };

  const saveTaxSettings = async (event) => {
    event.preventDefault();
    setTaxSettingsSaving(true);

    try {
      const response = await api.put(
        "/admin/invoice/tax-settings",
        {
          ...taxSettingsForm,
          cgst_percentage: Number(taxSettingsForm.cgst_percentage),
          sgst_percentage: Number(taxSettingsForm.sgst_percentage),
          igst_percentage: Number(taxSettingsForm.igst_percentage),
        },
        { headers: headers("edit") }
      );

      toast.success(response.data?.message || "Invoice tax settings saved.");
      setTaxSettingsOpen(false);
    } catch (error) {
      showError(error, "Failed to save invoice tax settings.");
    } finally {
      setTaxSettingsSaving(false);
    }
  };

  return (
    <section className="admin-page">
      <div className="page-header">
        <div>
          <p>Catalogue</p>
          <h1>HSN / SAC & GST</h1>
        </div>
        <button
          type="button"
          className="primary-action"
          onClick={openTaxSettings}
        >
          <TbSettings /> Tax settings
        </button>
      </div>

      <div className="page-card">
        <h2>{editingId ? "Edit tax class" : "Add tax class"}</h2>
        <p>Maintain the current GST rate here. Updated rates apply only to future orders.</p>
        <form className="product-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <label>
              Classification
              <select name="classification_type" value={form.classification_type}
                onChange={(e) => setForm({ ...form, classification_type: e.target.value })}>
                <option value="goods">Goods (HSN)</option>
                <option value="service">Service (SAC)</option>
              </select>
            </label>
            <label>
              HSN / SAC code
              <input value={form.hsn_sac_code} required
                onChange={(e) => setForm({ ...form, hsn_sac_code: e.target.value })} />
            </label>
            <label>
              Name
              <input value={form.name} required
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label>
              GST percentage
              <input type="number" min="0" max="100" step="0.01" value={form.gst_percentage} required
                onChange={(e) => setForm({ ...form, gst_percentage: e.target.value })} />
            </label>
            <label>
              Status
              <select value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <label>
              Description
              <input value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
          </div>
          <div className="page-actions">
            <button className="primary-action" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update tax class" : "Add tax class"}
            </button>
            {editingId && <button className="secondary-action" type="button" onClick={resetForm}>Cancel</button>}
          </div>
        </form>
      </div>

      <div className="page-card">
        <div className="page-header compact">
          <h2>Tax classes</h2>
          <input className="search-input" placeholder="Search code or name" value={search}
            onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Code</th>
                <th>Name</th>
                <th>GST</th>
                <th>Products</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item) => (
                <tr key={item.tax_class_id}>
                  <td>{item.classification_type === "service" ? "SAC" : "HSN"}</td>
                  <td><strong>{item.hsn_sac_code}</strong></td>
                  <td>{item.name}</td>
                  <td>{Number(item.gst_percentage || 0)}%</td>
                  <td>{Number(item.product_count || 0)}</td>
                  <td><span className={`status-badge ${item.status}`}>{item.status}</span></td>
                  {/* <td><div className="table-actions">
                    <button type="button" className="secondary-action" onClick={() => editItem(item)}>Edit</button>
                    <button type="button" className="danger-action" onClick={() => deleteItem(item)}>Delete</button>
                  </div></td> */}
                  <td style={{ textAlign: "right" }}>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="secondary-action"
                        onClick={() => editItem(item)}
                        title="Edit"
                        aria-label="Edit"
                      >
                        <FiEdit2 />
                      </button>

                      <button
                        type="button"
                        className="danger-action"
                        onClick={() => deleteItem(item)}
                        title="Delete"
                        aria-label="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && visibleItems.length === 0 && <tr><td colSpan="7">No tax classes found.</td></tr>}
              {loading && <tr><td colSpan="7">Loading...</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Sidebar
        visible={taxSettingsOpen}
        position="right"
        onHide={() => !taxSettingsSaving && setTaxSettingsOpen(false)}
        className="premium-user-sidebar invoice-tax-settings-sidebar"
      >
        <div className="sidebar-form-shell">
          <div className="sidebar-form-header">
            <span>Invoice configuration</span>
            <h2>Place of supply & fallback GST</h2>
            <p>
              Product rates come from the HSN/SAC master. These GST rates are
              used only for legacy products without a tax class. Existing
              invoice tax snapshots remain unchanged.
            </p>
          </div>

          {taxSettingsLoading ? (
            <div className="form-view-empty">
              <ProgressSpinner style={{ width: "42px", height: "42px" }} />
              <p>Loading tax settings...</p>
            </div>
          ) : (
            <form className="premium-sidebar-form" onSubmit={saveTaxSettings}>
              <div className="productform-grid invoice-tax-settings-grid">
                <label>
                  <span>Business name</span>
                  <input
                    required
                    value={taxSettingsForm.business_name}
                    onChange={(event) =>
                      updateTaxSettingsField("business_name", event.target.value)
                    }
                  />
                </label>

                <label>
                  <span>Base country</span>
                  <input
                    required
                    value={taxSettingsForm.origin_country}
                    onChange={(event) =>
                      updateTaxSettingsField("origin_country", event.target.value)
                    }
                  />
                </label>

                <label>
                  <span>Base state</span>
                  <input
                    required
                    value={taxSettingsForm.origin_state}
                    onChange={(event) =>
                      updateTaxSettingsField("origin_state", event.target.value)
                    }
                  />
                  <small>Orders matching this state use CGST and SGST.</small>
                </label>

                <label>
                  <span>LUT number</span>
                  <input
                    value={taxSettingsForm.lut_number}
                    onChange={(event) =>
                      updateTaxSettingsField("lut_number", event.target.value)
                    }
                    placeholder="Enter active LUT reference"
                  />
                  <small>Shown only on international export invoices.</small>
                </label>

                {[
                  ["cgst_percentage", "Fallback CGST rate (%)"],
                  ["sgst_percentage", "Fallback SGST rate (%)"],
                  ["igst_percentage", "Fallback IGST rate (%)"],
                ].map(([field, label]) => (
                  <label key={field}>
                    <span>{label}</span>
                    <input
                      required
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={taxSettingsForm[field]}
                      onChange={(event) =>
                        updateTaxSettingsField(field, event.target.value)
                      }
                    />
                  </label>
                ))}
              </div>

              <label className="invoice-tax-note-field">
                <span>International customer tax note</span>
                <textarea
                  required
                  rows="5"
                  value={taxSettingsForm.international_tax_note}
                  onChange={(event) =>
                    updateTaxSettingsField(
                      "international_tax_note",
                      event.target.value
                    )
                  }
                />
              </label>

              <div className="invoice-tax-rule-preview">
                <strong>Automatic rules</strong>
                <span>Same state: CGST + SGST</span>
                <span>Another Indian state: IGST</span>
                <span>Another country: GST 0%, export-under-LUT details shown</span>
                <small>All product prices are treated as tax-inclusive.</small>
              </div>

              <div className="sidebar-form-footer">
                <button
                  type="button"
                  className="ghost-action bottonborder"
                  disabled={taxSettingsSaving}
                  onClick={() => setTaxSettingsOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-action"
                  disabled={taxSettingsSaving}
                >
                  {taxSettingsSaving ? "Saving..." : "Save tax settings"}
                </button>
              </div>
            </form>
          )}
        </div>
      </Sidebar>
    </section>
  );
};

export default TaxClasses;
